/* Manic Fits — Manic AI fit assistant for the product page. */
import { StandardEvents } from '@shopify/events';

const sectionOf = (el) => el.closest('.shopify-section') || document;

/* ---------- Manic AI · Fit assistant ---------- */
/* Matches the shopper's body profile against this product's size chart.
   Signals: height, weight, build, fit preference and an optional tape measurement.
   Each signal places the shopper on the size scale; the signals are blended by how
   reliable they are, then the product's cut and fit note settle in-between cases. */
const PROFILE_KEY = 'manic-fit-profile';
const HOUSE_SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'];
/* Manic house sizing, used when a chart only lists garment measurements. Index 0 = S. */
const HOUSE = {
  height: [[63, 66], [65, 68], [67, 70], [69, 72], [71, 74]],
  weight: [[55, 65], [65, 75], [75, 85], [85, 95], [95, 110]],
  chest: [[33, 35], [36, 38], [39, 41], [42, 44], [45, 47]],
  waist: [[28, 30], [30, 33], [33, 36], [36, 39], [39, 42]],
};
const BUILD_WORDS = {
  slim: /lean|slim|skinny/i,
  athletic: /average|athletic|regular/i,
  muscular: /broad|muscular|athletic \/ broad/i,
  heavy: /broad|heavy|large|plus|bulky/i,
};
const BUILD_LABEL = { slim: 'slim', athletic: 'athletic', muscular: 'muscular', heavy: 'heavier' };
const PREF_SHIFT = { snug: -0.4, true: 0, relaxed: 0.4 };
/* How close (in sizes) a shopper must sit to a neighbouring size before it counts as a real second fit. */
const ALT_MAX_GAP = 0.65;
const FIT_WORDS = {
  compression: 'a compression fit that hugs the body',
  slim: 'a slim, body-hugging cut',
  regular: 'a regular athletic cut',
  relaxed: 'a relaxed, roomy cut',
  oversized: 'an oversized cut with extra drape',
};

const normSize = (s) => {
  const u = String(s || '').trim().toUpperCase();
  return u === 'XXL' ? '2XL' : u === 'XXXL' ? '3XL' : u;
};
const nums = (text) => (String(text).match(/\d+(?:\.\d+)?/g) || []).map(Number);
const fmtIn = (n) => `${Math.round(n * 2) / 2}"`;
const fmtHeight = (inches) => `${Math.floor(inches / 12)}'${Math.round(inches % 12)}"`;

function parseHeightInches(text) {
  const out = [];
  const re = /(\d)\s*'\s*(\d{1,2})?/g;
  let m;
  while ((m = re.exec(text))) out.push(Number(m[1]) * 12 + Number(m[2] || 0));
  return out;
}
function rangeOf(kind, text) {
  if (kind === 'height') {
    const h = parseHeightInches(text);
    if (!h.length) return null;
    return [h[0], h.length > 1 ? h[1] : h[0]];
  }
  const n = nums(text);
  if (!n.length) return null;
  if (kind === 'weight') {
    if (/upto|up to|under|below|</i.test(text)) return [n[0] - 10, n[0]];
    if (/\+|above|over/i.test(text)) return [n[0], n[0] + 12];
  }
  return [n[0], n.length > 1 ? n[1] : n[0]];
}

/* Where does a value sit on the size scale? 0 = first row, 1 = second row, fractions in between. */
function positionOn(ranges, v) {
  const pts = ranges.map((r, i) => (r ? { i, lo: r[0], hi: r[1], mid: (r[0] + r[1]) / 2 } : null)).filter(Boolean);
  if (pts.length < 2) return null;
  for (const p of pts) if (v >= p.lo && v <= p.hi) {
    // inside a size's range: lean towards neighbours near the edges
    const half = Math.max((p.hi - p.lo) / 2, 0.01);
    return p.i + 0.45 * Math.max(-1, Math.min(1, (v - p.mid) / half));
  }
  if (v < pts[0].mid) return pts[0].i - (pts[0].mid - v) / (pts[1].mid - pts[0].mid);
  const last = pts[pts.length - 1];
  const prev = pts[pts.length - 2];
  if (v > last.mid) return last.i + (v - last.mid) / (last.mid - prev.mid);
  for (let k = 0; k < pts.length - 1; k++) {
    const a = pts[k];
    const b = pts[k + 1];
    if (v >= a.mid && v <= b.mid) return a.i + ((v - a.mid) / (b.mid - a.mid)) * (b.i - a.i);
  }
  return null;
}

/* Body estimates calibrated on Manic's own charts (S ≈ 55–65 kg / 33–35" chest / 28–30" waist). */
function estimateChest(h, kg, build) {
  const adj = { slim: -1.5, athletic: 0, muscular: 2, heavy: 0.5 }[build] || 0;
  return 34.5 + 0.3 * (kg - 60) - 0.15 * (h - 67) + adj;
}
function estimateWaist(h, kg, build) {
  const adj = { slim: -1, athletic: -1, muscular: -0.5, heavy: 2 }[build] || 0;
  return 30 + 0.3 * (kg - 60) - 0.1 * (h - 67) + adj;
}

function readProfile() {
  try {
    return JSON.parse(localStorage.getItem(PROFILE_KEY) || 'null');
  } catch (e) {
    return null;
  }
}
function saveProfile(p) {
  try {
    localStorage.setItem(PROFILE_KEY, JSON.stringify({ ...p, ts: Date.now() }));
  } catch (e) {}
}
function forgetProfile() {
  try {
    localStorage.removeItem(PROFILE_KEY);
  } catch (e) {}
}

class ManicFitAI extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    const data = this.querySelector('script[type="application/json"]');
    if (!data) return;
    try {
      this.chart = JSON.parse(data.textContent);
    } catch (e) {
      return;
    }
    if (!this.chart || !Array.isArray(this.chart.rows) || this.chart.rows.length < 2) return;
    this.fit = this.dataset.fit || 'regular';
    this.audience = this.dataset.audience || 'men';
    this.rows = this.chart.rows;
    this.sizes = this.rows.map((r) => r.size);
    this.readColumns();
    if (!this.signalsAvailable()) return;
    this.ready = true;
    this.hidden = false;

    this.el = (sel) => this.querySelector(sel);
    this.measureKind = this.cols.bodyWaist > -1 && this.cols.bodyChest < 0 ? 'waist' : 'chest';
    const mLabel = this.el('[data-measure-label]');
    if (mLabel) mLabel.textContent = this.measureKind === 'waist' ? 'Waist (in)' : 'Chest (in)';
    const mTip = this.el('[data-measure-tip]');
    if (mTip)
      mTip.textContent =
        this.measureKind === 'waist'
          ? 'Measure around your natural waist, where your joggers sit.'
          : 'Measure around the fullest part of your chest, under the arms.';
    this.renderScale();

    this.addEventListener('click', (e) => {
      const chip = e.target.closest('[data-pick]');
      if (chip) {
        const group = chip.dataset.pick;
        const already = chip.getAttribute('aria-pressed') === 'true';
        this.querySelectorAll(`[data-pick="${group}"]`).forEach((c) => c.setAttribute('aria-pressed', 'false'));
        // build is optional, so a second tap clears it; preference always keeps one
        if (!(already && group === 'build')) chip.setAttribute('aria-pressed', 'true');
        this.update(true);
      }
      if (e.target.closest('[data-use-size]')) this.useSize(this.rec);
      if (e.target.closest('[data-use-alt]')) this.useSize(this.alt);
      if (e.target.closest('[data-edit]')) this.setCollapsed(false);
      if (e.target.closest('[data-forget]')) {
        forgetProfile();
        this.querySelectorAll('input[type="number"]').forEach((i) => (i.value = ''));
        this.querySelectorAll('select').forEach((s) => (s.selectedIndex = 0));
        this.querySelectorAll('[data-pick="build"]').forEach((c) => c.setAttribute('aria-pressed', 'false'));
        this.setPref('true');
        this.setCollapsed(false);
        this.update(false);
      }
    });
    this.addEventListener('input', () => this.update(true));
    this.addEventListener('change', () => this.update(true));

    // stock changes when the shopper switches colour
    this.section = sectionOf(this);
    this.onSelect = (event) => {
      if (!event.promise) return;
      event.promise.then(() => requestAnimationFrame(() => this.rec && this.paint())).catch(() => {});
    };
    this.section.addEventListener(StandardEvents.productSelect, this.onSelect);

    const saved = readProfile();
    if (saved && saved.ft && saved.kg) {
      this.fill(saved);
      this.update(false);
      if (this.rec) this.setCollapsed(true);
    } else {
      this.setPref('true');
    }
  }
  disconnectedCallback() {
    this.section && this.onSelect && this.section.removeEventListener(StandardEvents.productSelect, this.onSelect);
  }

  readColumns() {
    const cols = (this.chart.columns || []).map((c) => String(c).toLowerCase());
    const find = (fn) => cols.findIndex(fn);
    const isHalf = (c) => /½|1\/2|half/.test(c);
    const firstVal = (i) => (i > -1 ? String(this.rows[0].values[i] || '') : '');
    const chestIdx = find((c) => c.includes('chest') && !isHalf(c));
    const chestIsRange = chestIdx > -1 && nums(firstVal(chestIdx)).length > 1;
    this.cols = {
      height: find((c) => c.includes('height')),
      weight: find((c) => c.includes('weight')),
      build: find((c) => c.includes('build')),
      bodyChest: chestIsRange ? chestIdx : -1,
      garmentChest: find((c) => c.includes('chest') && isHalf(c)),
      garmentFullChest: chestIdx > -1 && !chestIsRange ? chestIdx : -1,
      bodyWaist: find((c) => c.includes('waist') && !isHalf(c)),
    };
    // house sizing stands in when the chart has no body measurements at all
    const c = this.cols;
    this.useHouse = c.height < 0 && c.weight < 0 && c.bodyChest < 0 && c.bodyWaist < 0;
    this.houseIndex = this.sizes.map((s) => HOUSE_SIZES.indexOf(normSize(s)) - 1);
  }
  signalsAvailable() {
    const c = this.cols;
    if (this.useHouse) return this.houseIndex.every((i) => i >= 0 && i < 5);
    return c.height > -1 || c.weight > -1 || c.bodyChest > -1 || c.bodyWaist > -1;
  }
  ranges(kind) {
    const map = { height: 'height', weight: 'weight', chest: 'bodyChest', waist: 'bodyWaist' };
    const col = this.cols[map[kind]];
    if (col > -1) return this.rows.map((r) => rangeOf(kind, r.values[col]));
    if (this.useHouse && HOUSE[kind]) return this.houseIndex.map((i) => HOUSE[kind][i] || null);
    return null;
  }

  /* ----- inputs ----- */
  fill(p) {
    const set = (name, v) => {
      const el = this.el(`[name="${name}"]`);
      if (el && v != null && v !== '') el.value = v;
    };
    set('ff-ft', p.ft);
    set('ff-in', p.in);
    set('ff-kg', p.kg);
    set('ff-measure', this.measureKind === 'waist' ? p.waist : p.chest);
    this.querySelectorAll('[data-pick="build"]').forEach((c) => c.setAttribute('aria-pressed', String(c.dataset.value === p.build)));
    this.setPref(p.pref || 'true');
  }
  setPref(v) {
    this.querySelectorAll('[data-pick="pref"]').forEach((c) => c.setAttribute('aria-pressed', String(c.dataset.value === v)));
  }
  readInputs() {
    const val = (n) => {
      const el = this.el(`[name="${n}"]`);
      return el ? el.value : '';
    };
    const pressed = (g) => {
      const c = this.querySelector(`[data-pick="${g}"][aria-pressed="true"]`);
      return c ? c.dataset.value : '';
    };
    const measure = Number(val('ff-measure')) || null;
    return {
      ft: Number(val('ff-ft')) || null,
      in: Number(val('ff-in')) || 0,
      kg: Number(val('ff-kg')) || null,
      build: pressed('build'),
      pref: pressed('pref') || 'true',
      chest: this.measureKind === 'chest' ? measure : (readProfile() || {}).chest || null,
      waist: this.measureKind === 'waist' ? measure : (readProfile() || {}).waist || null,
    };
  }

  /* ----- the engine ----- */
  recommend(p) {
    const h = p.ft ? p.ft * 12 + (p.in || 0) : null;
    const kg = p.kg;
    if (!h || !kg || kg < 30 || kg > 200) return null;
    const female = this.audience === 'women';
    const signals = [];
    const add = (kind, value, weight, label) => {
      const r = this.ranges(kind);
      if (!r || weight <= 0) return;
      const pos = positionOn(r, value);
      if (pos == null || Number.isNaN(pos)) return;
      signals.push({ kind, value, weight, pos, label, ranges: r });
    };

    add('weight', kg, 2, `weight (${kg} kg)`);
    add('height', h, 0.8, `height (${fmtHeight(h)})`);
    if (p.chest) add('chest', p.chest, 3.2, `chest (${fmtIn(p.chest)})`);
    else if (!female) add('chest', estimateChest(h, kg, p.build), p.build ? 1.4 : 1, `estimated chest (~${fmtIn(estimateChest(h, kg, p.build))})`);
    if (p.waist) add('waist', p.waist, 3.2, `waist (${fmtIn(p.waist)})`);
    else if (!female) add('waist', estimateWaist(h, kg, p.build), 0.9, `estimated waist (~${fmtIn(estimateWaist(h, kg, p.build))})`);

    if (this.cols.build > -1 && p.build && BUILD_WORDS[p.build]) {
      const hits = this.rows.map((r, i) => (BUILD_WORDS[p.build].test(r.values[this.cols.build]) ? i : -1)).filter((i) => i > -1);
      if (hits.length) {
        const pos = hits.reduce((a, b) => a + b, 0) / hits.length;
        signals.push({ kind: 'build', weight: 0.9, pos, label: `${BUILD_LABEL[p.build]} build` });
      }
    }
    if (!signals.length) return null;

    const W = signals.reduce((a, s) => a + s.weight, 0);
    const raw = signals.reduce((a, s) => a + s.weight * s.pos, 0) / W;
    const spread = Math.sqrt(signals.reduce((a, s) => a + s.weight * (s.pos - raw) ** 2, 0) / W);

    // the shopper's preference, and the product's own advice for in-between cases
    const note = String(this.chart.fit_note || '').toLowerCase();
    const shift = PREF_SHIFT[p.pref] || 0;
    let pos = raw + shift;
    const n = this.rows.length;
    const lowerIdx = Math.floor(pos);
    const frac = pos - lowerIdx;
    const between = frac > 0.3 && frac < 0.7 && lowerIdx >= 0 && lowerIdx < n - 1;
    let idx;
    let tieRule = '';
    if (between) {
      const saysUp = /between sizes?,? (?:go |size )?up|if between sizes, size up/.test(note);
      if (p.pref === 'snug') idx = lowerIdx;
      else if (p.pref === 'relaxed') idx = lowerIdx + 1;
      else if (saysUp || this.fit === 'compression') {
        idx = lowerIdx + 1;
        tieRule = 'up';
      } else if (this.fit === 'oversized' || this.fit === 'relaxed') {
        idx = lowerIdx;
        tieRule = 'down';
      } else idx = Math.round(pos);
    } else idx = Math.round(pos);
    const clamped = Math.max(0, Math.min(n - 1, idx));
    const outOfRange = pos < -0.6 ? 'below' : pos > n - 1 + 0.6 ? 'above' : '';

    const edge = 0.5 - Math.abs(raw - Math.round(raw));
    let confidence = 'high';
    if (outOfRange || spread > 0.9) confidence = 'low';
    else if (between || spread > 0.55 || edge < 0.15 || signals.length < 2) confidence = 'medium';

    return {
      size: this.sizes[clamped],
      index: clamped,
      base: Math.max(0, Math.min(n - 1, Math.round(raw))),
      pos: Math.max(-0.4, Math.min(n - 0.6, pos)),
      between: between ? [this.sizes[lowerIdx], this.sizes[lowerIdx + 1]] : null,
      tieRule,
      confidence,
      outOfRange,
      signals,
      profile: p,
      h,
    };
  }

  reasons(r) {
    const out = [];
    const i = r.base;
    const size = this.sizes[i];
    const inRange = (s) => s.ranges && s.ranges[i] && s.value >= s.ranges[i][0] - 0.3 && s.value <= s.ranges[i][1] + 0.3;
    const fmtRange = (s) => {
      const [a, b] = s.ranges[i];
      if (s.kind === 'height') return `${fmtHeight(a)}–${fmtHeight(b)}`;
      if (s.kind === 'weight') return `${Math.round(a)}–${Math.round(b)} kg`;
      return `${a}–${b}"`;
    };
    const ordered = [...r.signals].sort((a, b) => b.weight - a.weight);
    for (const s of ordered) {
      if (out.length >= 2) break;
      if (s.kind === 'build') continue;
      if (!s.ranges || !s.ranges[i]) continue;
      out.push(
        inRange(s)
          ? `Your ${s.label} sits inside ${size}'s ${fmtRange(s)} range.`
          : `Your ${s.label} is closest to ${size} (${fmtRange(s)}).`
      );
    }
    const g = this.cols.garmentChest > -1 ? this.cols.garmentChest : this.cols.garmentFullChest;
    if (g > -1) {
      const v = nums(this.rows[i].values[g])[0];
      if (v) {
        const around = this.cols.garmentChest > -1 ? v * 2 : v;
        out.push(`${r.size} measures ${fmtIn(around)} around the chest laid flat — ${FIT_WORDS[this.fit] || 'cut to fit'}.`);
      }
    } else if (FIT_WORDS[this.fit] && this.fit !== 'regular') {
      out.push(`This piece is ${FIT_WORDS[this.fit]}.`);
    }
    if (r.between) {
      const [a, b] = r.between;
      const why =
        r.profile.pref === 'snug'
          ? 'you asked for a snug fit'
          : r.profile.pref === 'relaxed'
            ? 'you asked for a relaxed fit'
            : r.tieRule === 'up'
              ? 'this one runs tight, so we size up'
              : r.tieRule === 'down'
                ? 'the cut is already roomy, so we size down'
                : 'it is the closer match';
      out.push(`You're between ${a} and ${b}. We picked ${r.size} because ${why}.`);
    } else if (r.index !== r.base) {
      out.push(`Moved from ${size} to ${r.size} for your ${r.profile.pref} fit preference.`);
    }
    if (r.outOfRange === 'above') out.push(`${r.size} is our largest size in this style, so expect a closer fit than usual.`);
    if (r.outOfRange === 'below') out.push(`${r.size} is our smallest size in this style, so expect a looser fit than usual.`);
    if (!r.profile.chest && !r.profile.waist && r.confidence !== 'high')
      out.push(`Add your ${this.measureKind} measurement for a sharper match.`);
    return out.slice(0, 4);
  }

  update(persist) {
    const p = this.readInputs();
    const r = this.recommend(p);
    this.last = r;
    this.rec = r ? r.size : null;
    if (r && persist) saveProfile(p);
    this.paint();
  }

  /* ----- stock awareness ----- */
  sizeInputs() {
    const section = sectionOf(this);
    return Array.from(section.querySelectorAll('variant-picker input[type="radio"]')).filter((i) => /size/i.test(i.dataset.optionName || ''));
  }
  inputFor(size) {
    const want = normSize(size);
    return this.sizeInputs().find((i) => normSize(i.value) === want) || null;
  }
  isAvailable(size) {
    const input = this.inputFor(size);
    return input ? input.dataset.optionAvailable !== 'false' : true;
  }
  markPicker(size) {
    this.sizeInputs().forEach((i) => {
      const label = i.closest('label');
      if (label) label.classList.toggle('manic-your-fit', !!size && normSize(i.value) === normSize(size));
    });
  }

  renderScale() {
    const scale = this.el('[data-scale]');
    if (!scale) return;
    scale.innerHTML = '';
    scale.style.setProperty('--n', this.sizes.length);
    this.sizes.forEach((s) => {
      const t = document.createElement('span');
      t.className = 'manic-ai__tick';
      t.dataset.tick = normSize(s);
      t.textContent = s;
      scale.appendChild(t);
    });
    const dot = document.createElement('i');
    dot.className = 'manic-ai__marker';
    dot.setAttribute('aria-hidden', 'true');
    scale.appendChild(dot);
  }

  paint() {
    const r = this.last;
    const result = this.el('[data-result]');
    const hint = this.el('[data-empty]');
    if (!r) {
      result.hidden = true;
      if (hint) hint.hidden = false;
      this.markPicker(null);
      return;
    }
    result.hidden = false;
    if (hint) hint.hidden = true;
    this.el('[data-size]').textContent = r.size;
    this.querySelectorAll('[data-size-label]').forEach((el) => (el.textContent = r.size));

    const conf = this.el('[data-confidence]');
    const confText = { high: 'High confidence', medium: 'Good match', low: 'Best guess' }[r.confidence];
    conf.textContent = r.between ? `Between ${r.between[0]} & ${r.between[1]}` : confText;
    conf.dataset.level = r.confidence;

    const n = this.sizes.length;
    const marker = this.el('.manic-ai__marker');
    if (marker) marker.style.left = `${((r.pos + 0.5) / n) * 100}%`;
    this.querySelectorAll('[data-tick]').forEach((t) => t.classList.toggle('is-active', t.dataset.tick === normSize(r.size)));

    const list = this.el('[data-reasons]');
    list.innerHTML = '';
    this.reasons(r).forEach((txt) => {
      const li = document.createElement('li');
      li.textContent = txt;
      list.appendChild(li);
    });

    const summary = this.el('[data-summary]');
    if (summary) {
      const p = r.profile;
      const bits = [fmtHeight(r.h), `${p.kg} kg`];
      if (p.build) bits.push(BUILD_LABEL[p.build]);
      if (p.pref !== 'true') bits.push(`${p.pref} fit`);
      summary.textContent = bits.join(' · ');
    }

    // stock: never a dead end
    const stock = this.el('[data-ai-stock]');
    const useBtn = this.el('[data-use-size]');
    const altBtn = this.el('[data-use-alt]');
    const available = this.isAvailable(r.size);
    // Stock never changes the pick: recommend() never looks at stock. When the pick is sold out,
    // a second size is offered only if the shopper's own numbers sit right on the line between
    // the two sizes, so it is a genuine fit and never a way to move leftover stock.
    this.alt = null;
    if (!available && r.confidence !== 'low' && !r.outOfRange) {
      const i = r.index;
      const k = r.pos >= i ? i + 1 : i - 1;
      if (k >= 0 && k < n && Math.abs(r.pos - k) <= ALT_MAX_GAP && this.isAvailable(this.sizes[k])) this.alt = this.sizes[k];
    }
    if (stock) {
      stock.hidden = available;
      if (!available) {
        stock.textContent = this.alt
          ? `${r.size} is your best fit and it's restocking. You sit right on the line, so ${this.alt} also fits you, ${near(this.sizes, r.size, this.alt) > 0 ? 'just a little roomier' : 'just a little closer-fitting'}.`
          : `${r.size} is your best fit and it's restocking. Tap Notify me and we'll email you the moment it's back.`;
      }
    }
    if (useBtn) useBtn.innerHTML = available ? `Select <span data-size-label>${r.size}</span>` : `Notify me for <span data-size-label>${r.size}</span>`;
    if (altBtn) {
      altBtn.hidden = !this.alt;
      if (this.alt) altBtn.textContent = `${this.alt} also fits`;
    }

    this.markPicker(r.size);
    document.dispatchEvent(new CustomEvent('manic:recommended-size', { detail: { size: r.size, confidence: r.confidence } }));
  }

  setCollapsed(on) {
    this.classList.toggle('is-known', on);
    const form = this.el('[data-form]');
    const welcome = this.el('[data-welcome]');
    if (form) form.hidden = on;
    if (welcome) welcome.hidden = !on;
  }

  useSize(size) {
    if (!size) return;
    const section = sectionOf(this);
    const input = this.inputFor(size);
    if (input && !input.checked) input.click();
    if (!input) {
      // dropdown pickers
      const select = Array.from(section.querySelectorAll('variant-picker select')).find((s) => {
        const lab = s.id && section.querySelector(`label[for="${s.id}"]`);
        return /size/i.test((lab && lab.textContent) || s.name || '');
      });
      if (select) {
        const opt = Array.from(select.options).find((o) => normSize(o.textContent) === normSize(size) || normSize(o.value) === normSize(size));
        if (opt) {
          select.value = opt.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }
    const picker = section.querySelector('variant-picker');
    picker && picker.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }
}
function near(sizes, a, b) {
  return sizes.indexOf(b) - sizes.indexOf(a);
}
if (!customElements.get('manic-fit-ai')) customElements.define('manic-fit-ai', ManicFitAI);
